import { ReactNode, useCallback, useEffect, useRef, type JSX } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { type FocusTargetOrFalse } from 'focus-trap';
import { ModalPortal, useAnimateInOutComponent, useReducedMotion, useScreenLock } from '@alltrails/core';
import IconButton from '@alltrails/denali/components/IconButton';
import Close from '@alltrails/denali/icons/Close';
import styles from './styles/styles.module.scss';

type FullscreenModalProps = {
  children: ReactNode;
  hideCloseButton?: boolean;
  hidePageScrollbarGutter?: boolean;
  initialFocus?: FocusTargetOrFalse;
  isOpen: boolean;
  isPaused?: boolean;
  modalClassName?: string;
  backgroundClassName?: string;
  closeButtonClassName?: string;
  ariaLabelElementId?: string;
  onCloseComplete?: () => void;
  onCloseRequest: () => void;
};

const FullscreenModal = ({
  children,
  hideCloseButton = false,
  hidePageScrollbarGutter,
  initialFocus,
  isOpen,
  isPaused = false,
  modalClassName,
  backgroundClassName,
  closeButtonClassName,
  ariaLabelElementId,
  onCloseRequest,
  onCloseComplete
}: FullscreenModalProps): JSX.Element | null => {
  const intl = useIntl();
  const { isAnimating, isVisible, onAnimationEnd } = useAnimateInOutComponent(isOpen, onCloseComplete);
  useScreenLock(isVisible, hidePageScrollbarGutter);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion && isOpen) {
      onAnimationEnd();
    }
    // Run once on mount to set opacity to 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep track of open state locally to avoid multiple onCloseRequest calls when a user clicks the close button - once for the button, a second time for onDeactivate.
  // This is preferred over a debounce because it handles the case where onCloseRequest changes the onCloseRequest definition, which can also lead to multiple calls.
  const isClosed = useRef(false);
  useEffect(() => {
    isClosed.current = !isOpen;
  }, [isOpen]);
  const closeModal = useCallback(() => {
    if (!isClosed.current) {
      onCloseRequest();
    }
    isClosed.current = true;
  }, [onCloseRequest]);

  if (!isAnimating && !isVisible) {
    return null;
  }

  return (
    <ModalPortal>
      <div
        className={classNames(styles.background, backgroundClassName, {
          [styles.animatedBackground]: !prefersReducedMotion,
          [styles.isAnimating]: isAnimating,
          [styles.isVisible]: isVisible
        })}
        onAnimationEnd={onAnimationEnd}
        id="modalBackground" // Needed by legacy hooks and components
        data-testid="modal-background"
      >
        <FocusTrap
          active={isVisible}
          paused={isPaused}
          focusTrapOptions={{
            escapeDeactivates: () => {
              closeModal();
              return false;
            },
            initialFocus
          }}
        >
          {/* Need to stop propagation of any animationEnd event within the modal so that it doesn't trigger the onAnimationEnd of the modalBackground component */}
          <div
            className={classNames(styles.modal, modalClassName)}
            role="dialog"
            aria-labelledby={ariaLabelElementId}
            onAnimationEnd={e => e.stopPropagation()}
          >
            {!hideCloseButton && (
              <IconButton
                className={classNames(styles.closeButton, closeButtonClassName)}
                icon={{ Component: Close }}
                onClick={closeModal}
                stopPropagation
                title={intl.formatMessage({ defaultMessage: 'Close' })}
                testId="modal-close"
                variant="elevated"
              />
            )}
            <div className={styles.scrollContainer} ref={scrollContainer}>
              <div>{children}</div>
            </div>
          </div>
        </FocusTrap>
      </div>
    </ModalPortal>
  );
};

export default FullscreenModal;
